import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Star, StarOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useBlogs } from '@/hooks/useBlogs';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { checkIsAdmin } from '@/lib/admin-utils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { normalizeMarkdown } from '@/lib/markdown-utils';
import { supabase } from '@/integrations/supabase/client';
import { BlogEditor } from './BlogEditor';

export default function BlogManagement() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const { 
    getAllBlogs, 
    deleteBlog, 
    togglePublish, 
    updateBlog 
  } = useBlogs();

  useEffect(() => {
    if (user?.id) {
      checkIsAdmin(user.id).then((result) => {
        setIsAdmin(result);
        setAdminChecked(true);
      });
    } else {
      setAdminChecked(true);
    }
  }, [user?.id]);
  
  const { data: allBlogs = [], isLoading, refetch } = getAllBlogs(isAdmin);
  const [editingBlog, setEditingBlog] = useState<any>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  if (!adminChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-destructive">Access denied. Admin privileges required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await deleteBlog.mutateAsync(id);
        toast.success('Blog post deleted successfully');
        refetch();
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete blog post');
      }
    }
  };

  const handleTogglePublish = async (id: string, published: boolean) => {
    try {
      await togglePublish.mutateAsync({ id, published });
      toast.success(`Blog post ${published ? 'published' : 'unpublished'} successfully`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${published ? 'publish' : 'unpublish'} blog post`);
    }
  };

  const handleToggleFeatured = async (id: string, featured: boolean) => {
    try {
      await updateBlog.mutateAsync({ id, featured });
      toast.success(`Blog post ${featured ? 'featured' : 'unfeatured'} successfully`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${featured ? 'feature' : 'unfeature'} blog post`);
    }
  };

  const handleReformatAll = async () => {
    if (!window.confirm('This will reformat the content of ALL blog posts to proper Markdown. This cannot be undone. Continue?')) return;
    let fixed = 0;
    let errors = 0;
    for (const blog of allBlogs) {
      const formatted = normalizeMarkdown(blog.content, true);
      console.log(`[Reformat] "${blog.title}" — original length: ${blog.content.length}, formatted length: ${formatted.length}, changed: ${formatted !== blog.content}`);
      if (formatted !== blog.content) {
        const { error } = await supabase
          .from('blogs')
          .update({ content: formatted })
          .eq('id', blog.id);
        if (error) {
          console.error(`[Reformat] Error updating "${blog.title}":`, error);
          errors++;
        } else {
          fixed++;
        }
      }
    }
    if (errors > 0) {
      toast.error(`Reformatted ${fixed} posts, but ${errors} failed. Check console for details.`);
    } else {
      toast.success(`Reformatted ${fixed} of ${allBlogs.length} blog posts.`);
    }
    refetch();
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Blog Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="flex items-center gap-2" onClick={handleReformatAll}>
            <RefreshCw className="w-4 h-4" />
            Reformat All Posts
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Create Blog Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Blog Post</DialogTitle>
                <DialogDescription>
                  Create a new blog post for the NexQR blog.
                </DialogDescription>
              </DialogHeader>
              <BlogEditor 
                onSave={() => {
                  setShowCreateDialog(false);
                  refetch();
                }}
                onCancel={() => setShowCreateDialog(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allBlogs.map((blog) => (
            <Card key={blog.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg line-clamp-2">{blog.title}</CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFeatured(blog.id, !blog.featured)}
                      className="h-8 w-8 p-0"
                    >
                      {blog.featured ? (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      ) : (
                        <StarOff className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTogglePublish(blog.id, !blog.published)}
                      className="h-8 w-8 p-0"
                    >
                      {blog.published ? (
                        <Eye className="w-4 h-4 text-green-500" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag: string, index: number) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {blog.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                  <span>{blog.read_time} min read</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setEditingBlog(blog)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(blog.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingBlog} onOpenChange={(open) => !open && setEditingBlog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Blog Post</DialogTitle>
            <DialogDescription>
              Update the content and settings of this blog post.
            </DialogDescription>
          </DialogHeader>
          {editingBlog && (
            <BlogEditor
              blog={editingBlog}
              onSave={() => {
                setEditingBlog(null);
                refetch();
              }}
              onCancel={() => setEditingBlog(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}